import React, { useMemo, useState } from "react";
import {
  Avatar, Box, Stack, Tooltip, Typography, alpha, useTheme,
  Popover, Dialog, DialogContent, IconButton, Divider, Button, useMediaQuery,
} from "@mui/material";
import WifiTetheringIcon from "@mui/icons-material/WifiTethering";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import CloseIcon from "@mui/icons-material/Close";
import LaunchIcon from "@mui/icons-material/Launch";
import { stripStaffNameSuffix } from "../../Core/util/staffName";

/**
 * Time-based staff schedule ("Today" page).
 *
 * Replaces the class-column matrix. Renders a horizontal time axis
 * (8am → 9pm by default, auto-widened to fit outliers) with one row
 * per staff-at-location. Class blocks are absolutely positioned by
 * actual start/end minutes; overlapping classes stack into lanes
 * within the same staff row instead of hiding each other.
 *
 * Data contract matches Today/index.tsx's OccurrenceRow — no
 * additional API fields are needed. Existing click behavior
 * (onOpenClass → per-occurrence Details page) is preserved.
 */

type Occurrence = {
  id: number;
  class_id: number;
  class_name: string | null;
  scheduled_date: string;
  start_time: string | null;
  end_time: string | null;
  staff_id: number | null;
  subject: string | null;
  grade: string | null;
  teaching_mode: "Online" | "Offline" | "Hybrid" | null;
  location_id: number | null;
  location_name: string | null;
  status: string | null;
  is_cancelled: boolean;
  is_makeup: boolean;
  capacity: number;
  assigned_count: number;
  available_spots: number;
  is_full: boolean;
  students: Array<{
    id: number;
    name: string;
    initials: string;
    image?: string | null;
    has_outstanding_invoice: boolean;
    // Per-student grade + subject for THIS occurrence, populated by
    // TimetableController::week from the student's
    // assign_student_classes.select_grade_subject_ids pivot. Multi-grade /
    // multi-subject classes therefore surface the exact pair the student
    // is enrolled in, not the class-level union. Optional so the timeline
    // stays defensive against older cached responses that predate the field.
    grade?: string | null;
    subject?: string | null;
  }>;
};

type StaffRow = {
  id: number;
  name: string;
  role: string | null;
  image: string | null;
  initials: string;
  weekly_class_count: number;
};

interface Props {
  staffRows: StaffRow[];
  occurrences: Occurrence[];
  selectedDate: string;
  onOpenClass: (occurrenceId: number) => void;
  canSeeFinance: boolean;
}

const HOUR_PX = 96;                 // Horizontal pixels per hour.
const STAFF_COL_PX = 240;           // Sticky left column width.
// Legacy uniform lane height — kept only as a floor for aesthetic
// consistency with very-short/empty blocks. Real block height is now
// computed per-occurrence from the number of student + available rows.
const LANE_MIN_PX = 46;
const TIME_HEADER_PX = 20;          // Height of the time-range header row inside a block.
const ROW_PX = 20;                  // Height of each student / available slot row.
const BLOCK_VPAD_PX = 4;            // Top+bottom padding inside a block.
const LANE_GAP_PX = 6;              // Vertical gap between stacked lanes.
const ROW_VPAD_PX = 8;              // Top/bottom padding inside a staff row.
const DEFAULT_START_HOUR = 8;       // Default timeline start (8:00 AM).
const DEFAULT_END_HOUR = 21;        // Default timeline end (9:00 PM).

/**
 * Compute the intrinsic pixel height of a class block from its content.
 * time header + one row per student + one green row per available spot.
 * Unlimited-capacity classes render only student rows (no fake availability).
 * A LANE_MIN_PX floor keeps zero-student / short blocks visually tidy.
 */
/**
 * Compose the display line for one student inside a class block. Prefers
 * the per-student (per-assignment) grade + subject the server returns for
 * THIS occurrence — multi-grade/multi-subject classes therefore surface
 * the specific pair the student is enrolled in, never the class-level
 * union. Falls back to the occurrence's class subject if the per-student
 * value is missing (older cached responses).
 */
function studentRowLabel(
  st: Occurrence["students"][number],
  occ: Occurrence,
): string {
  const parts: string[] = [st.name || "Student"];
  const grade = (st.grade || "").trim();
  if (grade) parts.push(grade);
  const subject = (st.subject || occ.subject || "").trim();
  if (subject) parts.push(subject);
  return parts.join(" · ");
}

/** Defensive cap for any tooltip/aria string. The API is not expected
 * to return anything close to 250 chars for these fields, but capping
 * guards against a rogue record blowing out the tooltip layout. */
const MAX_TOOLTIP_LEN = 250;
function capLen(s: string): string {
  return s.length > MAX_TOOLTIP_LEN ? s.slice(0, MAX_TOOLTIP_LEN - 1).trimEnd() + "…" : s;
}

/** Shared MUI Tooltip slot styling — portal-rendered so it escapes
 * the timeline's overflow container, wraps long text cleanly, and
 * sits above the schedule / popover / modal stack. */
const TOOLTIP_SLOT_PROPS = {
  tooltip: {
    sx: {
      maxWidth: 360,
      whiteSpace: "normal" as const,
      wordBreak: "break-word" as const,
      lineHeight: 1.45,
      fontSize: 12,
      bgcolor: "rgba(15,23,42,0.96)",
      color: "#fff",
      p: 1.25,
      borderRadius: "8px",
      boxShadow: "0 8px 24px rgba(15,23,42,0.28)",
    },
  },
  popper: {
    sx: { zIndex: 1600 },
  },
  arrow: {
    sx: { color: "rgba(15,23,42,0.96)" },
  },
};

function computeBlockHeight(occ: Occurrence): number {
  const students = Math.max(0, occ.students?.length ?? 0);
  const availableRows = occ.is_cancelled || occ.capacity <= 0
    ? 0
    : Math.max(0, occ.available_spots);
  const rows = students + availableRows;
  const raw = TIME_HEADER_PX + rows * ROW_PX + BLOCK_VPAD_PX * 2;
  return Math.max(LANE_MIN_PX, raw);
}

/** Parse "HH:MM:SS" or "HH:MM" into minutes since midnight. Returns null on bad input. */
function toMinutes(t: string | null | undefined): number | null {
  if (!t) return null;
  const m = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(t);
  if (!m) return null;
  const h = Number(m[1]);
  const mm = Number(m[2]);
  if (!Number.isFinite(h) || !Number.isFinite(mm)) return null;
  if (h < 0 || h > 23 || mm < 0 || mm > 59) return null;
  return h * 60 + mm;
}

function fmt12(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  const period = h >= 12 ? "PM" : "AM";
  const displayH = h % 12 === 0 ? 12 : h % 12;
  return m === 0 ? `${displayH} ${period}` : `${displayH}:${m.toString().padStart(2, "0")} ${period}`;
}

function fmtRange(startMin: number, endMin: number): string {
  return `${fmt12(startMin)} – ${fmt12(endMin)}`;
}

/**
 * Greedy interval lane packing. Given occurrences sorted by start_time,
 * assigns each to the lowest lane index that has no active overlap.
 * Returns the same list annotated with `lane` and the total lane count.
 */
function packLanes<T extends { startMin: number; endMin: number }>(
  items: T[],
): { items: Array<T & { lane: number }>; laneCount: number } {
  const laneEnds: number[] = [];
  const packed = items
    .slice()
    .sort((a, b) => a.startMin - b.startMin || a.endMin - b.endMin)
    .map((it) => {
      let assigned = -1;
      for (let i = 0; i < laneEnds.length; i++) {
        if (laneEnds[i] <= it.startMin) { assigned = i; break; }
      }
      if (assigned === -1) { assigned = laneEnds.length; laneEnds.push(it.endMin); }
      else { laneEnds[assigned] = it.endMin; }
      return { ...it, lane: assigned };
    });
  return { items: packed, laneCount: Math.max(1, laneEnds.length) };
}

const ScheduleTimeline: React.FC<Props> = ({
  staffRows, occurrences, onOpenClass,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // In-place details surface for the class the user clicked. Reuses
  // the fields already present in the occurrence — no extra fetch.
  // Popover on desktop (anchored to the clicked block), Dialog on
  // mobile/tablet (bigger tap target, better centered).
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [activeOccId, setActiveOccId] = useState<number | null>(null);
  const activeOcc = useMemo(
    () => occurrences.find((o) => o.id === activeOccId) || null,
    [occurrences, activeOccId],
  );
  const activeStaffName = useMemo(() => {
    if (!activeOcc) return null;
    const raw = staffRows.find((s) => s.id === activeOcc.staff_id)?.name || null;
    if (!raw) return null;
    // Same display-only cleanup as the row header — the modal must not
    // re-surface the parenthesised legacy location/role suffix.
    return stripStaffNameSuffix(raw) || raw;
  }, [activeOcc, staffRows]);
  const closeDetails = () => { setAnchorEl(null); setActiveOccId(null); };
  const openDetails = (e: React.MouseEvent<HTMLElement>, occId: number) => {
    setAnchorEl(e.currentTarget);
    setActiveOccId(occId);
  };

  /* ── Group occurrences by (location, staff), compute timeline bounds ── */
  const {
    groups, startHour, endHour, hourCount, totalMin,
  } = useMemo(() => {
    // Build once — staff name lookup for stable sorting.
    const staffName = new Map(staffRows.map((s) => [s.id, s.name]));

    // Bucket by locationKey → staffKey. locationKey uses -1 for null so
    // the "No Location" bucket sorts to the end deterministically.
    type Entry = { occ: Occurrence; startMin: number; endMin: number };
    const buckets = new Map<number, {
      locationName: string;
      byStaff: Map<number, Entry[]>;
    }>();

    let minStartMin: number | null = null;
    let maxEndMin: number | null = null;

    for (const o of occurrences) {
      const s = toMinutes(o.start_time);
      const e = toMinutes(o.end_time);
      if (s == null || e == null || e <= s) continue;

      minStartMin = minStartMin == null ? s : Math.min(minStartMin, s);
      maxEndMin = maxEndMin == null ? e : Math.max(maxEndMin, e);

      const locKey = o.location_id ?? -1;
      const locName = o.location_name || "No Location";
      const staffKey = o.staff_id ?? -1;

      if (!buckets.has(locKey)) buckets.set(locKey, { locationName: locName, byStaff: new Map() });
      const b = buckets.get(locKey)!;
      if (!b.byStaff.has(staffKey)) b.byStaff.set(staffKey, []);
      b.byStaff.get(staffKey)!.push({ occ: o, startMin: s, endMin: e });
    }

    // Timeline bounds — default 8am–9pm, widened to fit outliers, snapped to whole hours.
    const startHourResolved = Math.min(
      DEFAULT_START_HOUR,
      minStartMin != null ? Math.floor(minStartMin / 60) : DEFAULT_START_HOUR,
    );
    const endHourResolved = Math.max(
      DEFAULT_END_HOUR,
      maxEndMin != null ? Math.ceil(maxEndMin / 60) : DEFAULT_END_HOUR,
    );
    const hourCountResolved = Math.max(1, endHourResolved - startHourResolved);
    const totalMinResolved = hourCountResolved * 60;

    // Materialize groups: sorted locations, sorted staff, packed lanes per staff.
    const groupsResolved = Array.from(buckets.entries())
      .sort(([aKey, aVal], [bKey, bVal]) => {
        // "No Location" (-1) sinks; otherwise alphabetical by name.
        if (aKey === -1) return 1;
        if (bKey === -1) return -1;
        return aVal.locationName.localeCompare(bVal.locationName);
      })
      .map(([locKey, val]) => {
        const staffEntries = Array.from(val.byStaff.entries())
          .sort(([aKey, aList], [bKey, bList]) => {
            const nameA = staffName.get(aKey) || (aKey === -1 ? "Unassigned" : "");
            const nameB = staffName.get(bKey) || (bKey === -1 ? "Unassigned" : "");
            // Unassigned bucket sinks within a location.
            if (aKey === -1) return 1;
            if (bKey === -1) return -1;
            return nameA.localeCompare(nameB);
          })
          .map(([staffKey, entries]) => {
            const packed = packLanes(entries);
            // Compute intrinsic block height per lane so tall (many-student)
            // blocks push the whole staff row down instead of clipping. The
            // horizontal timeline math (HOUR_PX, pxPerMin, left/width) is
            // unchanged — only the vertical layout is content-driven now.
            const laneHeights: number[] = new Array(packed.laneCount).fill(LANE_MIN_PX);
            for (const it of packed.items) {
              const h = computeBlockHeight(it.occ);
              if (h > laneHeights[it.lane]) laneHeights[it.lane] = h;
            }
            const laneTops: number[] = [];
            let acc = ROW_VPAD_PX;
            for (let i = 0; i < laneHeights.length; i++) {
              laneTops.push(acc);
              acc += laneHeights[i] + (i < laneHeights.length - 1 ? LANE_GAP_PX : 0);
            }
            const rowHeight = acc + ROW_VPAD_PX;
            // Clean staff display name: same suffix stripping the Staff List
            // uses, so historical "Adam Khan (Scarb, Therapist)" reads as
            // "Adam Khan" here. Location is already shown as the section
            // banner — never inferred from the staff name.
            const rawName = staffName.get(staffKey) || (staffKey === -1 ? "Unassigned" : `Staff #${staffKey}`);
            return {
              staffKey,
              staffName: stripStaffNameSuffix(rawName) || rawName,
              staffMeta: staffRows.find((s) => s.id === staffKey) || null,
              classCount: entries.length,
              laneCount: packed.laneCount,
              laneHeights,
              laneTops,
              rowHeight,
              items: packed.items,
            };
          });
        return { locKey, locationName: val.locationName, staffRows: staffEntries };
      });

    return {
      groups: groupsResolved,
      startHour: startHourResolved,
      endHour: endHourResolved,
      hourCount: hourCountResolved,
      totalMin: totalMinResolved,
    };
  }, [occurrences, staffRows]);

  const timelineWidthPx = hourCount * HOUR_PX;
  const startMinAbsolute = startHour * 60;
  const pxPerMin = HOUR_PX / 60;

  /* ── Empty-day guard ─────────────────────────────────────────────── */
  if (groups.length === 0) {
    return (
      <Box sx={{ borderRadius: "16px", border: "1px dashed rgba(15,23,42,0.15)", bgcolor: "#fff", p: { xs: 3, md: 5 }, textAlign: "center" }}>
        <Typography sx={{ fontSize: 15, fontWeight: 800, color: "#0f172a", mb: 0.5 }}>No classes scheduled</Typography>
        <Typography sx={{ fontSize: 12.5, color: "#475569" }}>Nothing on the schedule for this date.</Typography>
      </Box>
    );
  }

  const hourLabels = Array.from({ length: hourCount + 1 }, (_, i) => (startHour + i) * 60);

  return (
    <Box
      sx={{
        position: "relative",
        border: "1px solid rgba(15,23,42,0.08)",
        borderRadius: "16px",
        overflow: "auto",
        bgcolor: "#fff",
        boxShadow: "0 1px 3px rgba(15,23,42,0.04)",
        maxHeight: "calc(100vh - 220px)",
        // Size the scroll container to the timeline content when the
        // viewport can hold it (so the rounded border wraps the
        // schedule tightly, no empty white gutter to the right of
        // the 9 PM boundary), and fall back to the available parent
        // width when it can't (so horizontal scroll kicks in).
        // `width: max-content` + `maxWidth: 100%` is the safest
        // cross-browser form of `min(100%, max-content)` and avoids
        // any CSS engine that trips on the `min()` function.
        width: "max-content",
        maxWidth: "100%",
        // No forced min-height — the container is exactly as tall as
        // its intrinsic content (sticky header + location banners +
        // staff rows). `maxHeight` above only bounds it on very tall
        // schedules; short days do not get padded to viewport height.
      }}
    >
      {/* Explicit width (not minWidth) so rows don't stretch past the
          last hour cell on wide viewports and leave "phantom" empty
          space after the final label. Horizontal scroll still kicks
          in when the viewport is narrower than this width. */}
      <Box sx={{ width: STAFF_COL_PX + timelineWidthPx, position: "relative" }}>
        {/* ── Sticky header row: STAFF cell + hour ticks ───────────── */}
        <Box
          sx={{
            display: "flex",
            position: "sticky", top: 0, zIndex: 3,
            bgcolor: "#F8FAFC",
            borderBottom: "1px solid rgba(15,23,42,0.1)",
            height: 42,
          }}
        >
          <Box
            sx={{
              width: STAFF_COL_PX, flex: `0 0 ${STAFF_COL_PX}px`,
              position: "sticky", left: 0, zIndex: 4,
              bgcolor: "#F8FAFC",
              borderRight: "1px solid rgba(15,23,42,0.1)",
              display: "flex", alignItems: "center",
              px: 2,
              boxShadow: "2px 0 4px -2px rgba(15,23,42,0.08)",
            }}
          >
            <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: 1.1, textTransform: "uppercase", color: "#334155" }}>
              Staff
            </Typography>
          </Box>
          <Box sx={{ position: "relative", width: timelineWidthPx, flex: `0 0 ${timelineWidthPx}px` }}>
            {hourLabels.map((mins, i) => (
              <Box
                key={mins}
                sx={{
                  position: "absolute", top: 0, bottom: 0,
                  left: i * HOUR_PX,
                  width: 1,
                  borderLeft: i === 0 ? "none" : "1px solid rgba(15,23,42,0.08)",
                }}
              />
            ))}
            {hourLabels.map((mins, i) => {
              const isEnd = i === hourLabels.length - 1;
              return (
                <Typography
                  key={mins}
                  sx={{
                    position: "absolute", top: 12,
                    // Labels are BOUNDARY labels — "9 AM" marks the vertical
                    // tick at x = i * HOUR_PX, not a column that starts to
                    // the right of it. Anchor each label's text edge AT its
                    // tick so a class that starts at 9:00 visually aligns
                    // with the "9 AM" label. The trailing label ("9 PM" by
                    // default) is right-anchored so its right edge sits on
                    // the final tick.
                    left: i * HOUR_PX,
                    transform: isEnd ? "translateX(-100%)" : "none",
                    paddingLeft: isEnd ? 0 : "3px",
                    paddingRight: isEnd ? "3px" : 0,
                    fontSize: 11,
                    fontWeight: isEnd ? 800 : 700,
                    color: isEnd ? "#94a3b8" : "#475569",
                    letterSpacing: 0.4,
                    pointerEvents: "none",
                    whiteSpace: "nowrap",
                  }}
                >
                  {fmt12(mins)}
                </Typography>
              );
            })}
          </Box>
        </Box>

        {/* ── Body: location banners + staff timeline rows ─────────── */}
        {groups.map((grp) => (
          <React.Fragment key={grp.locKey}>
            {/* Location banner — spans full timeline width; staff column
                remains sticky so the label is always visible. */}
            <Box sx={{ display: "flex", position: "relative" }}>
              <Box
                sx={{
                  width: STAFF_COL_PX, flex: `0 0 ${STAFF_COL_PX}px`,
                  position: "sticky", left: 0, zIndex: 2,
                  bgcolor: "#E8F1FA",
                  borderTop: "1px solid rgba(15,23,42,0.1)",
                  borderBottom: "1px solid rgba(15,23,42,0.1)",
                  borderRight: "1px solid rgba(15,23,42,0.1)",
                  display: "flex", alignItems: "center", px: 2,
                  py: 0.75,
                  boxShadow: "2px 0 4px -2px rgba(15,23,42,0.08)",
                }}
              >
                <Typography sx={{ fontSize: 11.5, fontWeight: 800, letterSpacing: 0.8, textTransform: "uppercase", color: "#0f3557" }}>
                  {grp.locationName}
                </Typography>
              </Box>
              <Box
                sx={{
                  width: timelineWidthPx, flex: `0 0 ${timelineWidthPx}px`,
                  bgcolor: "#E8F1FA",
                  borderTop: "1px solid rgba(15,23,42,0.1)",
                  borderBottom: "1px solid rgba(15,23,42,0.1)",
                }}
              />
            </Box>

            {grp.staffRows.map((sr) => {
              const rowHeight = sr.rowHeight;
              return (
                <Box key={`${grp.locKey}-${sr.staffKey}`} sx={{ display: "flex", position: "relative", borderBottom: "1px solid rgba(15,23,42,0.06)" }}>
                  {/* Sticky staff cell */}
                  <Box
                    sx={{
                      width: STAFF_COL_PX, flex: `0 0 ${STAFF_COL_PX}px`,
                      position: "sticky", left: 0, zIndex: 1,
                      bgcolor: "#fff",
                      borderRight: "1px solid rgba(15,23,42,0.1)",
                      display: "flex", alignItems: "center", gap: 1.25,
                      px: 2, py: 1,
                      boxShadow: "2px 0 4px -2px rgba(15,23,42,0.06)",
                    }}
                  >
                    <Avatar
                      src={sr.staffMeta?.image || undefined}
                      sx={{ width: 32, height: 32, fontSize: 12, fontWeight: 700, bgcolor: "#0C5A46", color: "#fff" }}
                    >
                      {sr.staffMeta?.initials || sr.staffName.slice(0, 2).toUpperCase()}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a", lineHeight: 1.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={sr.staffName}>
                        {sr.staffName}
                      </Typography>
                      <Typography sx={{ fontSize: 10.5, color: "#64748b", fontWeight: 600, mt: 0.25 }}>
                        {sr.classCount} Class{sr.classCount === 1 ? "" : "es"}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Timeline area */}
                  <Box sx={{ position: "relative", width: timelineWidthPx, flex: `0 0 ${timelineWidthPx}px`, height: rowHeight, bgcolor: "#fff" }}>
                    {/* Hour separators */}
                    {hourLabels.map((mins, i) => (
                      <Box
                        key={mins}
                        sx={{
                          position: "absolute", top: 0, bottom: 0,
                          left: i * HOUR_PX,
                          width: 1,
                          borderLeft: i === 0 ? "none" : "1px solid rgba(15,23,42,0.06)",
                          pointerEvents: "none",
                        }}
                      />
                    ))}

                    {/* Class blocks */}
                    {sr.items.map((entry) => {
                      const { occ, startMin, endMin, lane } = entry;
                      // Exact positioning — no cosmetic gutter and NO
                      // minimum-width clamp. The backend allows classes
                      // as short as 15 min (ClassScheduleController.php:
                      // max(15, ...)), and 15 min × 1.6 px/min = 24 px,
                      // well below any padded floor. A minimum width
                      // would visually END the block later than its
                      // real end_time, misrepresenting the schedule.
                      // Readability for short blocks is handled by the
                      // existing Tooltip (full time/name/roster on
                      // hover + keyboard focus) and by overflow:hidden.
                      const left = (startMin - startMinAbsolute) * pxPerMin;
                      const width = (endMin - startMin) * pxPerMin;
                      const top = sr.laneTops[lane];
                      const blockHeight = computeBlockHeight(occ);

                      const isOnline = occ.teaching_mode === "Online";
                      const isCancelled = occ.is_cancelled;
                      // Available rows only rendered when capacity is
                      // known + finite. Null/unlimited (<=0) → no fake rows.
                      const availableRows = isCancelled || occ.capacity <= 0
                        ? 0
                        : Math.max(0, occ.available_spots);

                      const accent = isCancelled ? "#94a3b8"
                        : isOnline ? "#3B82F6"
                        : "#0C5A46";
                      const headerBg = isCancelled ? alpha("#94a3b8", 0.15)
                        : isOnline ? alpha("#3B82F6", 0.12)
                        : alpha("#0C5A46", 0.10);

                      const primaryStudent = occ.students[0]?.name || occ.class_name || "Class";

                      // Block-level hover preview — quick glance without
                      // opening the details popover. Fields come from the
                      // same occurrence object (no extra fetch). Long
                      // strings are capped at 250 chars for safety.
                      const staffNameForBlock = sr.staffName;
                      const uniqueGrades = Array.from(new Set(
                        occ.students.map((s) => (s.grade || "").trim()).filter(Boolean),
                      ));
                      const uniqueSubjects = Array.from(new Set(
                        occ.students
                          .map((s) => (s.subject || occ.subject || "").trim())
                          .filter(Boolean),
                      ));
                      const rosterStr = capLen(occ.students.map((s) => s.name).join(", "));
                      const gradesStr = capLen(uniqueGrades.join(", "));
                      const subjectsStr = capLen(uniqueSubjects.join(", "));
                      const capacityStr = isCancelled
                        ? "Cancelled"
                        : occ.capacity > 0
                          ? `${occ.assigned_count} / ${occ.capacity}${occ.available_spots > 0 ? `  ·  ${occ.available_spots} available` : ""}`
                          : "Unlimited";

                      const blockTooltip = (
                        <Box>
                          <Typography sx={{ fontSize: 12.5, fontWeight: 800, mb: 0.5 }}>
                            {fmtRange(startMin, endMin)}
                          </Typography>
                          {staffNameForBlock && (
                            <Typography sx={{ fontSize: 11.5, opacity: 0.9 }}>
                              <Box component="span" sx={{ opacity: 0.7 }}>Staff:</Box> {capLen(staffNameForBlock)}
                            </Typography>
                          )}
                          {rosterStr && (
                            <Typography sx={{ fontSize: 11.5, opacity: 0.9 }}>
                              <Box component="span" sx={{ opacity: 0.7 }}>
                                {occ.students.length === 1 ? "Student:" : `Students (${occ.students.length}):`}
                              </Box> {rosterStr}
                            </Typography>
                          )}
                          {gradesStr && (
                            <Typography sx={{ fontSize: 11.5, opacity: 0.9 }}>
                              <Box component="span" sx={{ opacity: 0.7 }}>{uniqueGrades.length > 1 ? "Grades:" : "Grade:"}</Box> {gradesStr}
                            </Typography>
                          )}
                          {subjectsStr && (
                            <Typography sx={{ fontSize: 11.5, opacity: 0.9 }}>
                              <Box component="span" sx={{ opacity: 0.7 }}>{uniqueSubjects.length > 1 ? "Subjects:" : "Subject:"}</Box> {subjectsStr}
                            </Typography>
                          )}
                          <Typography sx={{ fontSize: 11.5, opacity: 0.9 }}>
                            <Box component="span" sx={{ opacity: 0.7 }}>Capacity:</Box> {capacityStr}
                          </Typography>
                          <Typography sx={{ fontSize: 10.5, opacity: 0.65, mt: 0.75, fontStyle: "italic" }}>
                            Click to view full details
                          </Typography>
                        </Box>
                      );

                      return (
                        <Tooltip
                          key={occ.id}
                          title={blockTooltip}
                          arrow
                          placement="top"
                          enterDelay={450}
                          leaveDelay={0}
                          slotProps={TOOLTIP_SLOT_PROPS}
                        >
                          <Box
                            role="button"
                            tabIndex={0}
                            aria-label={`Class ${fmtRange(startMin, endMin)}, ${primaryStudent}`}
                            onClick={(e) => openDetails(e, occ.id)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openDetails(e as unknown as React.MouseEvent<HTMLElement>, occ.id); }
                            }}
                            sx={{
                              position: "absolute",
                              left, width, top, height: blockHeight,
                              borderRadius: "8px",
                              bgcolor: "#fff",
                              border: `1px solid ${alpha(accent, 0.35)}`,
                              borderLeft: `3px solid ${accent}`,
                              cursor: "pointer",
                              overflow: "hidden",
                              display: "flex", flexDirection: "column",
                              transition: "transform 120ms ease, box-shadow 120ms ease",
                              "&:hover": {
                                transform: "translateY(-1px)",
                                boxShadow: `0 4px 10px ${alpha(accent, 0.25)}`,
                                zIndex: 2,
                              },
                              "&:focus-visible": {
                                outline: `2px solid ${accent}`,
                                outlineOffset: 2,
                              },
                              opacity: isCancelled ? 0.7 : 1,
                              textDecoration: isCancelled ? "line-through" : "none",
                            }}
                          >
                            {/* ── Time header ─────────────────────────── */}
                            <Box sx={{
                              height: TIME_HEADER_PX,
                              flex: `0 0 ${TIME_HEADER_PX}px`,
                              bgcolor: headerBg,
                              borderBottom: `1px solid ${alpha(accent, 0.25)}`,
                              display: "flex", alignItems: "center",
                              px: 0.75, gap: 0.5,
                            }}>
                              <Typography sx={{ fontSize: 10, fontWeight: 800, color: accent, letterSpacing: 0.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", flex: "1 1 auto" }}>
                                {fmtRange(startMin, endMin)}
                              </Typography>
                              {isOnline && (
                                <Tooltip title="Online" arrow>
                                  <WifiTetheringIcon sx={{ fontSize: 11, color: "#3B82F6", flexShrink: 0 }} />
                                </Tooltip>
                              )}
                              {occ.is_makeup && (
                                <Tooltip title="Makeup class" arrow>
                                  <AutorenewIcon sx={{ fontSize: 11, color: "#a16207", flexShrink: 0 }} />
                                </Tooltip>
                              )}
                              {isCancelled && (
                                <Tooltip title="Cancelled" arrow>
                                  <EventBusyIcon sx={{ fontSize: 11, color: "#64748b", flexShrink: 0 }} />
                                </Tooltip>
                              )}
                            </Box>

                            {/* ── Student rows ────────────────────────
                                One row per enrolled student. Subject is
                                the class-relevant subject (from the
                                per-assignment pivot on the API) — not
                                the student's system-wide subject set.
                                Each row wraps in a MUI Tooltip so a
                                truncated "Gabrielle Rodr…" still reveals
                                the full Name / Grade / Subject on hover
                                or keyboard focus. Tooltip is portalled
                                so the block's `overflow: hidden` cannot
                                clip it. */}
                            {occ.students.map((st) => {
                              const fullName = capLen((st.name || "Student").trim());
                              const gradeStr = capLen((st.grade || "").trim());
                              const subjectStr = capLen(((st.subject || occ.subject || "").trim()));
                              const compactLabel = studentRowLabel(st, occ);
                              const ariaLabel = capLen(compactLabel);

                              const studentTooltip = (
                                <Box>
                                  <Typography sx={{ fontSize: 10.5, fontWeight: 700, opacity: 0.7, textTransform: "uppercase", letterSpacing: 0.5 }}>
                                    Student
                                  </Typography>
                                  <Typography sx={{ fontSize: 12.5, fontWeight: 800, mb: gradeStr || subjectStr ? 0.75 : 0 }}>
                                    {fullName}
                                  </Typography>
                                  {gradeStr && (
                                    <>
                                      <Typography sx={{ fontSize: 10.5, fontWeight: 700, opacity: 0.7, textTransform: "uppercase", letterSpacing: 0.5 }}>
                                        Grade
                                      </Typography>
                                      <Typography sx={{ fontSize: 12, mb: subjectStr ? 0.75 : 0 }}>
                                        {gradeStr}
                                      </Typography>
                                    </>
                                  )}
                                  {subjectStr && (
                                    <>
                                      <Typography sx={{ fontSize: 10.5, fontWeight: 700, opacity: 0.7, textTransform: "uppercase", letterSpacing: 0.5 }}>
                                        Subject / Program
                                      </Typography>
                                      <Typography sx={{ fontSize: 12 }}>
                                        {subjectStr}
                                      </Typography>
                                    </>
                                  )}
                                </Box>
                              );

                              return (
                                <Tooltip
                                  key={st.id}
                                  title={studentTooltip}
                                  arrow
                                  placement="top"
                                  enterDelay={200}
                                  leaveDelay={0}
                                  slotProps={TOOLTIP_SLOT_PROPS}
                                >
                                  <Box
                                    tabIndex={0}
                                    aria-label={ariaLabel}
                                    sx={{
                                      height: ROW_PX,
                                      flex: `0 0 ${ROW_PX}px`,
                                      borderBottom: "1px solid rgba(15,23,42,0.06)",
                                      display: "flex",
                                      alignItems: "center",
                                      px: 0.75,
                                      // minWidth on the flex-item parent is
                                      // essential — without it, the child
                                      // Typography's `overflow: hidden` +
                                      // `textOverflow: ellipsis` will not
                                      // trigger inside a flex layout.
                                      minWidth: 0,
                                      width: "100%",
                                      outline: "none",
                                      "&:focus-visible": {
                                        bgcolor: "rgba(79,70,229,0.08)",
                                      },
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontSize: 10.5,
                                        fontWeight: 600,
                                        color: "#0f172a",
                                        lineHeight: 1.2,
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        flex: "1 1 auto",
                                        minWidth: 0,
                                        width: "100%",
                                      }}
                                    >
                                      {compactLabel}
                                    </Typography>
                                  </Box>
                                </Tooltip>
                              );
                            })}

                            {/* ── Available rows — one green rectangle per
                                open spot, matching the old workflow. Full
                                class = zero rows (no chip needed). Cancelled
                                or unlimited-capacity classes never render
                                Available rows. */}
                            {Array.from({ length: availableRows }).map((_, idx) => (
                              <Box key={`avail-${idx}`} sx={{
                                height: ROW_PX,
                                flex: `0 0 ${ROW_PX}px`,
                                borderBottom: idx < availableRows - 1 ? "1px solid rgba(15,23,42,0.06)" : "none",
                                bgcolor: alpha("#22c55e", 0.15),
                                color: "#166534",
                                display: "flex", alignItems: "center", justifyContent: "center",
                              }}>
                                <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.3 }}>
                                  Available
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </Tooltip>
                      );
                    })}
                  </Box>
                </Box>
              );
            })}
          </React.Fragment>
        ))}
      </Box>

      {/* ── Class detail surface ────────────────────────────────────
             Reuses the fields already on the clicked occurrence — no
             extra request. Popover on desktop (anchored to the block
             for context), Dialog on mobile (bigger tap surface). */}
      {activeOcc && (() => {
        const startMin = toMinutes(activeOcc.start_time) ?? 0;
        const endMin = toMinutes(activeOcc.end_time) ?? 0;
        const modeLabel =
          activeOcc.teaching_mode === "Online" ? "Online"
          : activeOcc.teaching_mode === "Hybrid" ? "Hybrid"
          : "In-Person";
        // Capacity is rendered as "enrolled / total" so it always agrees
        // with the class block's green Available rectangles. When capacity
        // is 0/null the class is unlimited (matches the block's "no fake
        // availability" branch above) → show "Unlimited" rather than "N / 0".
        const enrolledCount = activeOcc.assigned_count;
        const capacityLabel = activeOcc.is_cancelled
          ? "Cancelled"
          : activeOcc.capacity > 0
            ? `${enrolledCount} / ${activeOcc.capacity}`
            : "Unlimited";

        const Body = (
          <Box sx={{ p: 2.25, minWidth: { xs: 0, md: 320 }, maxWidth: 380 }}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 1 }}>
              <Box>
                <Typography sx={{ fontSize: 10, fontWeight: 800, letterSpacing: 1.1, textTransform: "uppercase", color: "#64748b" }}>
                  Class Details
                </Typography>
                <Typography sx={{ fontSize: 15, fontWeight: 800, color: "#0f172a", lineHeight: 1.3, mt: 0.25 }}>
                  {fmtRange(startMin, endMin)}
                </Typography>
              </Box>
              <IconButton size="small" onClick={closeDetails} aria-label="Close details" sx={{ mt: -0.5, mr: -0.5 }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>

            <Divider sx={{ my: 1.25 }} />

            <Stack spacing={1}>
              {activeOcc.class_name && (
                <DetailRow label="Class" value={activeOcc.class_name} />
              )}
              {activeOcc.students.length > 0 && (
                <DetailRow
                  label={activeOcc.students.length === 1 ? "Student" : `Students (${activeOcc.students.length})`}
                  value={activeOcc.students.map((s) => s.name).join(", ")}
                />
              )}
              {activeOcc.grade && (
                <DetailRow label="Grade" value={activeOcc.grade} />
              )}
              {activeOcc.subject && (
                <DetailRow label="Subject / Program" value={activeOcc.subject} />
              )}
              {activeStaffName && (
                <DetailRow label="Staff" value={activeStaffName} />
              )}
              {activeOcc.location_name && (
                <DetailRow label="Location" value={activeOcc.location_name} />
              )}
              <DetailRow label="Study Mode" value={modeLabel} />
              <DetailRow label="Capacity" value={capacityLabel} />
              {activeOcc.is_makeup && (
                <DetailRow label="Type" value="Makeup class" />
              )}
              {activeOcc.status && (
                <DetailRow label="Status" value={activeOcc.status} />
              )}
            </Stack>

            <Divider sx={{ mt: 1.75, mb: 1.25 }} />

            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button size="small" onClick={closeDetails} sx={{ textTransform: "none" }}>
                Close
              </Button>
              <Button
                size="small"
                variant="contained"
                endIcon={<LaunchIcon sx={{ fontSize: 14 }} />}
                onClick={() => {
                  const id = activeOcc.id;
                  closeDetails();
                  onOpenClass(id);
                }}
                sx={{ textTransform: "none", bgcolor: "#0C5A46", "&:hover": { bgcolor: "#0a4838" } }}
              >
                View full page
              </Button>
            </Stack>
          </Box>
        );

        return isMobile ? (
          <Dialog open onClose={closeDetails} maxWidth="xs" fullWidth>
            <DialogContent sx={{ p: 0 }}>{Body}</DialogContent>
          </Dialog>
        ) : (
          <Popover
            open
            anchorEl={anchorEl}
            onClose={closeDetails}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            transformOrigin={{ vertical: "top", horizontal: "center" }}
            slotProps={{ paper: { sx: { borderRadius: "12px", boxShadow: "0 12px 32px rgba(15,23,42,0.18)" } } }}
          >
            {Body}
          </Popover>
        );
      })()}
    </Box>
  );
};

/** Compact label / value row for the details surface. Values wrap
 * naturally so long student, subject, and location names are never
 * truncated inside the popover. */
const DetailRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Stack direction="row" spacing={1.5} alignItems="flex-start">
    <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.5, minWidth: 96, flexShrink: 0, pt: "1px" }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#0f172a", lineHeight: 1.35, wordBreak: "break-word" }}>
      {value}
    </Typography>
  </Stack>
);

export default ScheduleTimeline;
